import { CustomButton } from "@/components/custom/custom-button";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { showToast } from "@/redux/actions/toast-actions";
import { createAccountSchema } from "@/schemas/ledger.schema";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Stack,
  TextField,
  styled,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLedgerEnv } from "@/hooks/use-ledger-env";
import { useCreateLedgerAccount } from "@/hooks/use-ledger-accounts";
import {
  ACCOUNT_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  GL_ACCOUNT_CATALOG,
  GL_ACCOUNT_OPTIONS,
  GL_ROLE_LABELS,
} from "@/pages/ledger/data";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 480,
    width: "100%",
  },
}));

const CreateAccountDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { environment } = useLedgerEnv();
  const mutation = useCreateLedgerAccount();

  const formik = useFormik({
    initialValues: {
      accountNumber: "",
      accountName: "",
      accountType: "",
      currency: "NGN",
      role: "",
    },
    validationSchema: createAccountSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate(
        // role is null (not "") for accounts with no special operational role,
        // matching the backend `role` column.
        { ...values, role: values.role || null, environment },
        {
          onSuccess: (data) => {
            dispatch(showToast(data?.message ?? "Account created", "success"));
            const created = data?.data?.accountNumber ?? values.accountNumber;
            formik.resetForm();
            onClose();
            // Redirect to detail so they see balance = 0 and the actions.
            navigate(`/ledger/accounts/${created}`);
          },
          onError: (err) => {
            // 409 = duplicate account number in this env: target the field.
            if (err?.statusCode === 409) {
              formik.setFieldError(
                "accountNumber",
                "An account with this number already exists in this environment.",
              );
              formik.setFieldTouched("accountNumber", true, false);
              return;
            }
            dispatch(
              showToast(err?.message ?? "Failed to create account", "error"),
            );
          },
        },
      );
    },
  });

  // Reset when reopened so a stale 409 error never lingers.
  useEffect(() => {
    if (open) {
      formik.resetForm();
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  // Picking a ledger name auto-fills (and locks) every other field from the
  // catalog, so the account number / type / role can never be mistyped.
  const handleGlSelect = (e) => {
    const gl = GL_ACCOUNT_CATALOG.find(
      (g) => g.accountNumber === e.target.value,
    );
    if (!gl) return;
    formik.setValues({
      accountNumber: gl.accountNumber,
      accountName: gl.name,
      accountType: gl.accountType,
      currency: gl.currency,
      role: gl.role ?? "",
    });
  };

  const hasSelection = Boolean(formik.values.accountNumber);
  const selectedTypeLabel =
    ACCOUNT_TYPE_OPTIONS.find((o) => o.value === formik.values.accountType)
      ?.label ?? "";
  const selectedRoleLabel = formik.values.role
    ? (GL_ROLE_LABELS[formik.values.role] ?? formik.values.role)
    : "No special role";

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Create Ledger Account
          </DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1} mt={0.75}>
            <Typography variant="body2" color="text.secondary">
              Creating in
            </Typography>
            <Chip
              label={environment}
              size="small"
              sx={{
                height: 22,
                fontWeight: 600,
                fontSize: "0.7rem",
                backgroundColor:
                  environment === "SANDBOX" ? "#fffbeb" : "#eef2ff",
                color: environment === "SANDBOX" ? "#92400e" : "#3730a3",
              }}
            />
          </Stack>
        </Box>

        <Stack spacing={2}>
          <FormSelectInput
            formik={formik}
            name="accountNumber"
            label="GL Account (Ledger Name)"
            options={GL_ACCOUNT_OPTIONS}
            onChange={handleGlSelect}
          />

          {/* Auto-filled from the selected GL — read-only so the number, type
              and role always match the chosen ledger. */}
          <TextField
            fullWidth
            size="small"
            label="Account Number"
            value={formik.values.accountNumber || ""}
            placeholder="Select a GL above"
            disabled
          />
          <TextField
            fullWidth
            size="small"
            label="Account Type"
            value={selectedTypeLabel}
            placeholder="—"
            disabled
          />
          <TextField
            fullWidth
            size="small"
            label="Role"
            value={hasSelection ? selectedRoleLabel : ""}
            placeholder="—"
            disabled
          />
          <FormSelectInput
            formik={formik}
            name="currency"
            label="Currency"
            options={CURRENCY_OPTIONS}
            isDisabled={!hasSelection}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 1, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Creating..." : "Create Account"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default CreateAccountDialog;
